import { alpha, lighten, darken, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

/**
 * Panel design tokens — derived entirely from the live MUI theme so the
 * "AnooreHR Panel" widgets recolor automatically with the tenant brand color,
 * the chosen palette, and light/dark mode. Nothing here is hardcoded to a
 * specific hue: `a` is always `theme.palette.primary.main`, and `a2..a4` are
 * tonal tints of it (the "flowing" single-hue family from the approved mockup).
 */
export interface PanelTokens {
  /** Brand accent (primary) and three graduated tints for multi-metric charts. */
  a: string;
  a2: string;
  a3: string;
  a4: string;
  /** Low-alpha accent for ring/bar tracks. */
  aSoft: string;
  /** Positive / status green (+ soft track). */
  positive: string;
  positiveSoft: string;
  /** Muted negative (for expense bars / down deltas). */
  negative: string;
  /** Near-ink text and muted label. */
  ink: string;
  label: string;
  /** Surfaces & lines. */
  card: string;
  border: string;
  /** Chart gridlines and ring/bar track fill (theme-aware). */
  grid: string;
  track: string;
  /** Warning / amber tone (maps to theme.palette.warning.main). */
  amber: string;
  amberDark: string;
  isDark: boolean;
}

/** Pure derivation so it can be used outside React (e.g. building SVG strings). */
export function derivePanelTokens(theme: Theme): PanelTokens {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  // Tonal tints: in light mode we lighten toward white; in dark mode the
  // accent reads better when tints stay vivid, so we lighten more gently.
  const step = isDark ? 0.14 : 0.18;
  const a = primary;
  const a2 = lighten(primary, step);
  const a3 = lighten(primary, step * 2);
  const a4 = lighten(primary, step * 3);
  return {
    a,
    a2,
    a3,
    a4,
    aSoft: alpha(primary, isDark ? 0.24 : 0.1),
    positive: theme.palette.success.main,
    positiveSoft: alpha(theme.palette.success.main, isDark ? 0.22 : 0.14),
    negative: isDark ? lighten(theme.palette.error.main, 0.1) : darken(theme.palette.error.main, 0.05),
    ink: theme.palette.text.primary,
    label: theme.palette.text.secondary,
    card: theme.palette.background.paper,
    border: theme.palette.divider,
    grid: theme.palette.divider,
    track: isDark ? alpha('#FFFFFF', 0.07) : alpha('#0F172A', 0.05),
    amber: theme.palette.warning.main,
    amberDark: darken(theme.palette.warning.main, 0.15),
    isDark,
  };
}

/** Hook form for use inside components. */
export function usePanelTokens(): PanelTokens {
  return derivePanelTokens(useTheme());
}
